import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Loader2, User as UserIcon, Mail, Shield, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    company: user?.company || "",
    phone: user?.phone || "",
  });
  const [saving, setSaving] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      toast.success("Profile updated");
    } catch {
      toast.error("Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      setUser(null);
      toast.success("Your account and data have been permanently deleted.");
      navigate("/");
    } catch {
      toast.error("Could not delete account");
    } finally {
      setDeleting(false);
    }
  };

  const field = "mt-1.5 rounded-sm border-border bg-transparent focus:border-primary focus:ring-primary";
  const lbl = "font-mono2 text-xs uppercase tracking-widest text-muted-foreground";

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-8">
        <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">Account</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tighter sm:text-4xl">Your profile</h1>

        <div className="mt-8 flex items-center gap-4 border border-border p-6">
          <div className="flex h-16 w-16 items-center justify-center border border-border bg-secondary/40">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-full w-full object-cover" />
            ) : (
              <UserIcon className="h-7 w-7 text-primary" />
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate font-heading text-xl font-semibold">{user?.name}</p>
            <p className="flex items-center gap-1.5 font-mono2 text-xs text-muted-foreground">
              <Mail className="h-3.5 w-3.5" /> {user?.email}
            </p>
            <p className="mt-1 flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">
              <Shield className="h-3 w-3" /> {user?.role} · {user?.auth_provider} login
            </p>
          </div>
        </div>

        <form onSubmit={save} className="mt-6 space-y-5 border border-border p-6">
          <div>
            <Label className={lbl}>Full name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" className={field} />
          </div>
          <div>
            <Label className={lbl}>Company</Label>
            <Input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="profile-company" placeholder="Optional" className={field} />
          </div>
          <div>
            <Label className={lbl}>Phone</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone" placeholder="Optional" className={field} />
          </div>
          <Button type="submit" disabled={saving} data-testid="save-profile-btn"
            className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
          </Button>
        </form>

        {/* GDPR: danger zone */}
        <div className="mt-8 border border-destructive/40 p-6" data-testid="danger-zone">
          <p className="flex items-center gap-2 font-mono2 text-xs uppercase tracking-widest text-destructive"><AlertTriangle className="h-4 w-4" /> Danger zone</p>
          <h3 className="mt-2 font-heading text-lg font-bold">Delete your account &amp; data</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Permanently removes your account, projects, generations and forum posts. This cannot be undone.
          </p>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="delete-account-btn"
                className="mt-4 rounded-sm border-destructive text-destructive hover:bg-destructive hover:text-white">
                <Trash2 className="mr-2 h-4 w-4" /> Delete my account
              </Button>
            </DialogTrigger>
            <DialogContent className="border-border">
              <DialogHeader><DialogTitle className="font-heading">Permanently delete your account?</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">
                This will erase all your data. Type <span className="font-mono2 font-bold text-destructive">DELETE</span> to confirm.
              </p>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-confirm-input"
                placeholder="DELETE" className="rounded-sm border-border bg-transparent" />
              <DialogFooter>
                <Button onClick={deleteAccount} disabled={confirmText !== "DELETE" || deleting} data-testid="delete-confirm-btn"
                  className="rounded-sm bg-destructive font-mono2 uppercase tracking-widest text-white hover:bg-destructive/90">
                  {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Delete Forever
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
